import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Skeleton, Table, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { fetchSupplierOrderPZDocuments } from '../api/supplierOrdersApi';
import type { SupplierOrderPZDocumentSummaryDto } from '../../../dto/pzTransportCandidates';
import { getPZOperationalStatus } from '../../warehouse/warehouseDocumentOperationalStatus';
import { PZStatus } from '../../../types/pz';
import { formatDate } from '../../../utils/dateUtils';

/**
 * 2026-08-11 (PZ generowane z transportu — karta ZD, zakładka Powiązania):
 * the PZ documents already issued off this supplier order. Read-only, like
 * its sibling `SupplierOrderDeliveryTransportsSection` — the documents
 * themselves are owned by the PZ module, this card only answers "what has
 * already been received off this ZD" from the supplier-order side.
 */
interface SupplierOrderPZDocumentsSectionProps {
  supplierOrderId?: string;
}

function getPZStatusColor(status: PZStatus): string {
  switch (status) {
    case PZStatus.DRAFT:
      return 'default';
    case PZStatus.CONFIRMED:
      return 'processing';
    case PZStatus.RECEIVED:
      return 'success';
    case PZStatus.CANCELLED:
      return 'error';
    default:
      return 'default';
  }
}

const SupplierOrderPZDocumentsSection: React.FC<SupplierOrderPZDocumentsSectionProps> = ({
  supplierOrderId,
}) => {
  const { t } = useTranslation(['orders', 'pz']);
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<SupplierOrderPZDocumentSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const loadDocuments = useCallback(async () => {
    if (!supplierOrderId) {
      return;
    }

    setLoading(true);
    setLoadFailed(false);
    try {
      setDocuments(await fetchSupplierOrderPZDocuments(supplierOrderId));
    } catch (error) {
      // Advisory only, same as the delivery-transports section: a failure
      // here never blocks the rest of the supplier order page.
      setDocuments([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [supplierOrderId]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  if (loading) {
    return (
      <div data-testid="supplier-order-pz-documents-loading">
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      </div>
    );
  }

  if (loadFailed) {
    return (
      <Alert
        type="warning"
        showIcon
        data-testid="supplier-order-pz-documents-error"
        message={t('orders:supplierOrders.detail.pzDocumentsLoadFailed')}
        action={(
          <Button size="small" icon={<ReloadOutlined />} onClick={() => { void loadDocuments(); }}>
            {t('common:retry', { defaultValue: 'Ponów' })}
          </Button>
        )}
      />
    );
  }

  if (documents.length === 0) {
    return (
      <Typography.Text
        type="secondary"
        data-testid="supplier-order-pz-documents-empty"
      >
        {t('orders:supplierOrders.detail.pzDocumentsEmpty')}
      </Typography.Text>
    );
  }

  return (
    <Table
      rowKey="id"
      size="middle"
      pagination={false}
      dataSource={documents}
      data-testid="supplier-order-pz-documents-list"
      columns={[
        {
          title: t('orders:supplierOrders.detail.pzDocumentsColumns.pzNumber'),
          key: 'pzNumber',
          render: (_: unknown, record: SupplierOrderPZDocumentSummaryDto) => (
            <Button type="link" onClick={() => navigate(`/app/pz/${record.id}`)}>
              {record.pzNumber}
            </Button>
          ),
        },
        {
          title: t('orders:supplierOrders.detail.pzDocumentsColumns.status'),
          key: 'status',
          render: (_: unknown, record: SupplierOrderPZDocumentSummaryDto) => {
            const operationalStatus = getPZOperationalStatus(record.status as PZStatus);
            return (
              <Tag color={getPZStatusColor(operationalStatus)}>
                {t(`pz:status.${operationalStatus.toLowerCase()}`)}
              </Tag>
            );
          },
        },
        {
          title: t('orders:supplierOrders.detail.pzDocumentsColumns.receiptDate'),
          key: 'receiptDate',
          render: (_: unknown, record: SupplierOrderPZDocumentSummaryDto) => (
            record.receiptDate ? formatDate(record.receiptDate) : '-'
          ),
        },
        {
          title: t('orders:supplierOrders.detail.pzDocumentsColumns.itemCount'),
          key: 'itemCount',
          align: 'right' as const,
          render: (_: unknown, record: SupplierOrderPZDocumentSummaryDto) => record.itemCount,
        },
      ]}
    />
  );
};

export default SupplierOrderPZDocumentsSection;
